/**
 * 게시판 창 — 공지사항 · 업무자료실 (회사 안에서만). 이전 시스템 boardList.jsp 자리
 * 목록 → 글 보기(첨부 내려받기·댓글) → 쓰기/고치기(첨부 올리기)
 * 공지 쓰기·위에 고정은 회사관리자, 자료실은 누구나 / 고치기·지우기는 쓴 사람 또는 회사관리자
 */
import React, { useEffect, useState } from 'react';
import {
  BoardKind, BoardRow, BoardPost, BoardFile, BoardComment, boardList, boardGet, boardSave, boardDelete, boardUpload, boardFileDelete,
  boardDownload, boardComment, boardCommentDelete, errMsg,
} from '../utils/api';
import { overlay, box, head, xBtn, inp, area, btn, smBtn, chip, th, td, C } from './ui';
import { useAuth } from './AuthGate';

const KIND: [BoardKind, string][] = [['NOTICE', '공지사항'], ['FILE', '업무자료실']];
const day = (s: string) => (s || '').slice(0, 10);
const when = (s: string) => (s || '').replace('T', ' ').slice(0, 16);
const size = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);
const MAX = 7 * 1024 * 1024;
const readFile = (f: File) => new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsDataURL(f); });

type View = { at: 'list' } | { at: 'post'; id: number } | { at: 'edit'; id?: number };

const BoardDialog: React.FC<{ onClose: () => void; initialKind?: BoardKind; initialPost?: number }> = ({ onClose, initialKind = 'NOTICE', initialPost }) => {
  const { user } = useAuth();
  const manager = user.role === 'SUPER' || user.role === 'OWNER';
  const [kind, setKind] = useState<BoardKind>(initialKind);
  const [view, setView] = useState<View>(initialPost ? { at: 'post', id: initialPost } : { at: 'list' });
  const canWrite = kind === 'FILE' || manager;
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 860, height: '88vh' }}>
        <div style={head()}><b>게시판</b><button onClick={onClose} style={xBtn}>✕</button></div>
        <div style={{ display: 'flex', gap: 6, padding: '0 24px 10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {KIND.map(([k, l]) => <button key={k} onClick={() => { setKind(k); setView({ at: 'list' }); }} style={chip(kind === k)}>{l}</button>)}
          {view.at === 'list' && canWrite && <button onClick={() => setView({ at: 'edit' })} style={{ ...btn(), marginLeft: 'auto', height: 34 }}>글쓰기</button>}
          {view.at !== 'list' && <button onClick={() => setView({ at: 'list' })} style={{ ...btn('#8B95A1'), marginLeft: 'auto', height: 34 }}>목록</button>}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 20px', fontSize: 14 }}>
          {view.at === 'list' && <List kind={kind} onOpen={id => setView({ at: 'post', id })} />}
          {view.at === 'post' && <Post id={view.id} onEdit={() => setView({ at: 'edit', id: view.id })} onGone={() => setView({ at: 'list' })} onKind={setKind} />}
          {view.at === 'edit' && <Editor id={view.id} kind={kind} manager={manager} onDone={id => setView(id ? { at: 'post', id } : { at: 'list' })} />}
        </div>
      </div>
    </div>
  );
};

const List: React.FC<{ kind: BoardKind; onOpen: (id: number) => void }> = ({ kind, onOpen }) => {
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const load = (p = page, query = q) => boardList(kind, query, p).then(r => { setRows(r.data.rows); setTotal(r.data.total); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { setPage(1); setRows(null); load(1, q); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const pages = Math.max(1, Math.ceil(total / 20));
  return (<>
    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
      <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && (setPage(1), load(1, q))} placeholder="제목·내용 검색" style={{ ...inp, width: 220 }} />
      <button onClick={() => { setPage(1); load(1, q); }} style={btn(C.blue, false)}>검색</button>
    </div>
    {err && <div style={{ color: C.red, fontSize: 13 }}>⚠ {err}</div>}
    {rows === null ? <div style={{ color: C.mute }}>불러오는 중…</div> : rows.length === 0 ? <div style={{ color: C.mute, padding: '12px 0' }}>글이 없습니다</div> : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['제목', '쓴 사람', '날짜', '조회'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.POST_ID} onClick={() => onOpen(r.POST_ID)} style={{ cursor: 'pointer', background: r.PIN_YN === 'Y' ? C.blueWeak : undefined }}>
            <td style={{ ...td, fontWeight: r.PIN_YN === 'Y' ? 700 : 500 }}>
              {r.PIN_YN === 'Y' && '📌 '}{r.TITLE}
              {r.FILES > 0 && <span style={{ color: C.mute, fontSize: 12 }}> 📎{r.FILES}</span>}
              {r.CMTS > 0 && <span style={{ color: C.blue, fontSize: 12 }}> [{r.CMTS}]</span>}
            </td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.USER_NM || r.USER_ID}</td>
            <td style={{ ...td, whiteSpace: 'nowrap', color: C.mute }}>{day(r.REG_DT)}</td>
            <td style={{ ...td, color: C.mute }}>{r.VIEWS}</td>
          </tr>
        ))}</tbody>
      </table>
    )}
    {pages > 1 && <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 12 }}>
      {Array.from({ length: pages }, (_, i) => i + 1).map(p => <button key={p} onClick={() => { setPage(p); load(p); }} style={{ ...chip(p === page), height: 30, padding: '0 11px' }}>{p}</button>)}
    </div>}
  </>);
};

const Post: React.FC<{ id: number; onEdit: () => void; onGone: () => void; onKind: (k: BoardKind) => void }> = ({ id, onEdit, onGone, onKind }) => {
  const { user } = useAuth();
  const manager = user.role === 'SUPER' || user.role === 'OWNER';
  const [d, setD] = useState<{ post: BoardPost; files: BoardFile[]; comments: BoardComment[] } | null>(null);
  const [cmt, setCmt] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const load = () => boardGet(id).then(r => { setD(r.data); onKind(r.data.post.KIND); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (f: () => Promise<unknown>) => { try { await f(); await load(); } catch (e) { setErr(errMsg(e)); } };
  if (!d) return <div style={{ color: err ? C.red : C.mute }}>{err ?? '불러오는 중…'}</div>;
  const p = d.post;
  return (<div>
    <h2 style={{ fontSize: 20, margin: '6px 0 4px', letterSpacing: -0.3 }}>{p.PIN_YN === 'Y' && '📌 '}{p.TITLE}</h2>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: C.mute, fontSize: 13, flexWrap: 'wrap' }}>
      <span>{p.USER_NM || p.USER_ID} · {when(p.REG_DT)} · 조회 {p.VIEWS}</span>
      {p.CAN_EDIT && <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
        <button onClick={onEdit} style={smBtn(C.blue, false)}>고치기</button>
        <button onClick={() => { if (window.confirm('이 글을 지울까요? 첨부 파일도 함께 지워집니다.')) boardDelete(id).then(onGone).catch(e => setErr(errMsg(e))); }} style={smBtn('#F04452', false)}>지우기</button>
      </span>}
    </div>
    {err && <div style={{ color: C.red, fontSize: 13, marginTop: 8 }}>⚠ {err}</div>}
    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, padding: '16px 0', borderBottom: `1px solid ${C.line}`, minHeight: 80, wordBreak: 'break-word' }}>{p.BODY}</div>
    {d.files.length > 0 && <div style={{ padding: '12px 0', borderBottom: `1px solid ${C.line}` }}>
      {d.files.map(f => (
        <div key={f.FILE_ID} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
          <button onClick={() => boardDownload(f).catch(e => setErr(errMsg(e)))} style={{ border: 'none', background: 'none', color: C.blue, cursor: 'pointer', padding: 0, fontSize: 14, textAlign: 'left' }}>📎 {f.FILE_NM}</button>
          <span style={{ color: C.mute, fontSize: 12 }}>{size(f.FILE_SIZE)}</span>
        </div>
      ))}
    </div>}
    <div style={{ padding: '12px 0' }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>댓글 {d.comments.length}</div>
      {d.comments.map(m => (
        <div key={m.CMT_ID} style={{ padding: '8px 0', borderBottom: `1px solid ${C.fill}` }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <b style={{ fontSize: 13 }}>{m.USER_NM || m.USER_ID}</b><span style={{ color: C.mute, fontSize: 12 }}>{when(m.REG_DT)}</span>
            {(manager || m.USER_ID === user.id) && <button onClick={() => window.confirm('댓글을 지울까요?') && run(() => boardCommentDelete(m.CMT_ID))}
              style={{ marginLeft: 'auto', border: 'none', background: 'none', color: C.mute, cursor: 'pointer', fontSize: 12 }}>지우기</button>}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{m.BODY}</div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <textarea value={cmt} onChange={e => setCmt(e.target.value)} rows={2} maxLength={1000} placeholder="댓글" style={{ ...area, flex: 1 }} />
        <button disabled={!cmt.trim()} onClick={() => run(() => boardComment(id, cmt.trim())).then(() => setCmt(''))} style={{ ...btn(), height: 'auto' }}>등록</button>
      </div>
    </div>
  </div>);
};

const Editor: React.FC<{ id?: number; kind: BoardKind; manager: boolean; onDone: (id?: number) => void }> = ({ id, kind, manager, onDone }) => {
  const [f, setF] = useState({ title: '', body: '', pin: false });
  const [files, setFiles] = useState<BoardFile[]>([]);
  const [adds, setAdds] = useState<File[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [postKind, setPostKind] = useState(kind);
  useEffect(() => {
    if (!id) return;
    boardGet(id).then(r => { const p = r.data.post; setF({ title: p.TITLE, body: p.BODY || '', pin: p.PIN_YN === 'Y' }); setFiles(r.data.files); setPostKind(p.KIND); })
      .catch(e => setErr(errMsg(e)));
  }, [id]);
  const pick = (list: FileList | null) => {
    const all = Array.from(list || []);
    const big = all.filter(x => x.size > MAX);
    if (big.length) setErr(`7MB가 넘는 파일은 올릴 수 없습니다: ${big.map(x => x.name).join(', ')}`); else setErr(null);
    setAdds(a => [...a, ...all.filter(x => x.size <= MAX)].slice(0, Math.max(0, 10 - files.length)));
  };
  const save = async () => {
    if (!f.title.trim()) { setErr('제목을 입력하세요'); return; }
    setBusy('저장 중…'); setErr(null);
    try {
      const pid = await boardSave({ id, kind: postKind, title: f.title.trim(), body: f.body, pin: f.pin });
      for (let i = 0; i < adds.length; i++) {
        setBusy(`첨부 올리는 중 (${i + 1}/${adds.length}) ${adds[i].name}`);
        await boardUpload(pid, adds[i].name, await readFile(adds[i]));
      }
      onDone(pid);
    } catch (e) { setErr(errMsg(e)); setBusy(null); }
  };
  return (<div>
    <div style={{ fontSize: 13, color: C.mute, margin: '4px 0 10px' }}>{postKind === 'NOTICE' ? '공지사항' : '업무자료실'} {id ? '글 고치기' : '새 글'}</div>
    <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="제목" maxLength={200} style={{ ...inp, width: '100%', height: 44, fontSize: 16, marginBottom: 8 }} autoFocus />
    <textarea value={f.body} onChange={e => setF({ ...f, body: e.target.value })} placeholder="내용" rows={14} style={{ ...area, width: '100%', boxSizing: 'border-box' }} />
    {manager && <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '8px 0', fontSize: 14, cursor: 'pointer' }}>
      <input type="checkbox" checked={f.pin} onChange={e => setF({ ...f, pin: e.target.checked })} />📌 목록 맨 위에 고정</label>}
    <div style={{ margin: '10px 0', padding: 12, background: C.fill, borderRadius: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>첨부 파일 <span style={{ color: C.mute, fontWeight: 400, fontSize: 12 }}>(파일당 7MB, 글 하나에 10개까지)</span></div>
      {files.map(x => (
        <div key={x.FILE_ID} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
          <span>📎 {x.FILE_NM}</span><span style={{ color: C.mute, fontSize: 12 }}>{size(x.FILE_SIZE)}</span>
          <button onClick={() => window.confirm(`${x.FILE_NM}을(를) 지울까요?`) && boardFileDelete(x.FILE_ID).then(() => setFiles(files.filter(y => y.FILE_ID !== x.FILE_ID))).catch(e => setErr(errMsg(e)))}
            style={smBtn('#F04452', false)}>지우기</button>
        </div>
      ))}
      {adds.map((x, i) => (
        <div key={`${x.name}${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
          <span style={{ color: C.blue }}>+ {x.name}</span><span style={{ color: C.mute, fontSize: 12 }}>{size(x.size)}</span>
          <button onClick={() => setAdds(adds.filter((_, j) => j !== i))} style={smBtn('#8B95A1')}>빼기</button>
        </div>
      ))}
      {files.length + adds.length < 10 && <input type="file" multiple onChange={e => { pick(e.target.files); e.target.value = ''; }} style={{ marginTop: 6, fontSize: 13 }} />}
    </div>
    {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>⚠ {err}</div>}
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
      {busy && <span style={{ color: C.mute, fontSize: 13 }}>{busy}</span>}
      <button onClick={() => onDone(id)} disabled={!!busy} style={btn('#8B95A1')}>취소</button>
      <button onClick={save} disabled={!!busy} style={btn()}>{id ? '고치기' : '올리기'}</button>
    </div>
  </div>);
};

export default BoardDialog;
